import { Channel, deviceUtils } from 'scbk-services';

import {
  SecureKeypadContext,
  SecureKeypadDriver,
  SecureKeypadFieldId,
  SecureKeypadFieldOption,
} from '../types/types';
import { formatUtils } from 'scbk-shared-module/utils';

export class AstxKeypadDriver implements SecureKeypadDriver {
  private initialized = false;

  private optionRegistry = new Map<SecureKeypadFieldId, SecureKeypadFieldOption>();

  private elementRegistry = new Map<SecureKeypadFieldId, HTMLElement>();

  private observeValueChange(el: HTMLInputElement, callback: () => {}) {
    const proto = HTMLInputElement.prototype;
    const desc = Object.getOwnPropertyDescriptor(proto, 'value');

    if (!desc?.get || !desc?.set) return;
    Object.defineProperty(el, 'value', {
      get() {
        return desc.get!.call(this);
      },
      set(v: string) {
        desc.set!.call(this, v);

        queueMicrotask(() => {
          callback();
        });
      },
      configurable: true,
    });
  }

  readonly id = 'astx2';

  constructor() {}

  /**
   * 지원여부
   * @param ctx 컨텍스트
   * @returns
   */
  supported(ctx: SecureKeypadContext): boolean {
    // 모바일 뱅킹, 인터넷 뱅킹
    // 인터넷 뱅킹 && OS : Windows  일 때)
    // if (import.meta.env.VITE_ENV === 'local') {
    //   return false;
    // }
    return !ctx.virtualOnly && ctx.channel === Channel.IB && deviceUtils.isWin();
  }

  /**
   * 초기화 수행
   * @param ctx
   */
  init(ctx: SecureKeypadContext) {
    // console.log('# AstxKeypadDriver init ctx');

    if (typeof window === 'undefined') {
      return;
    }

    this.initOnce(ctx);
  }

  destroy() {
    this.initialized = false;
    this.optionRegistry.clear();
    this.elementRegistry.clear();
  }

  /**
   * 키패드 등록
   * @param el 입력요소
   * @param option 키패드 옵션
   * @returns
   */
  register(el: HTMLInputElement, option?: SecureKeypadFieldOption) {
    const w = window as any;
    w.$ASTX2 = w.$ASTX2 ?? {};
    if (!el) {
      return;
    }
    const _opt = this.mergeOptions(el.id, option);
    if (!_opt) {
      return;
    }
    this.optionRegistry.set(_opt.fieldId, _opt);

    w.$_astxu.addEvent(el, 'click', () => {
      console.log('### clear');
      w.$ASTX2.clearE2EText(el, function () {
        console.log('[보안키패드]', '### clearE2EInput ASTX2', el.id);
        el.dispatchEvent(new Event('input', { bubbles: true }));
      });
    });

    w.$_astxu.addEvent(el, 'keyup', async () => {
      _opt.setEncData?.(); // 암호화 데이터 저장
    });

    // 버튼 등으로 인한 변경 값 감지
    this.observeValueChange(el, async () => {
      _opt.setEncData?.(); // 암호화 데이터 저장
    });

    if (w.$ASTX2 && typeof w.$ASTX2.addE2EObject === 'function') {
      const dataEnc = el.getAttribute('data-enc');
      if (dataEnc === 'num') {
        // num 인경우 숫자만 입력
        el.setAttribute('e2e_inputtype', '1');
      }
      w.$ASTX2.addE2EObject(el, '2');
    }

    // disabled 이어도 흰색배경 적용
    el.classList.add('not-disabled');

    this.elementRegistry.set(el.id, el);
  }

  /**
   * 키패드 unregister
   * @param el
   */
  unregister(el: HTMLInputElement) {
    this.elementRegistry.delete(el.id);
    const w = window as any;
    w.$ASTX2 = w.$ASTX2 ?? {};
    // 초기화 ~
    w.$ASTX2.resetE2E(
      el.id, // custcode (for SDK)
      function onComplete(result: string) {
        w.$_astxu.log('[onComplete] resetE2E result=' + result);
      },
    );
  }

  /**
   * 키패드 open
   * @param fieldId 필드 ID
   * @returns
   */
  async open(fieldId: SecureKeypadFieldId): Promise<void> {
    const el = this.elementRegistry.get(fieldId) as HTMLInputElement;
    if (!el) {
      return;
    }

    const w = window as any;
    w.$ASTX2 = w.$ASTX2 ?? {};

    w.$ASTX2.clearE2EText(el, function () {
      console.log('[보안키패드]', '### clearE2EInput ASTX2', el.id);
      el.value = '';
    });

    el.focus();
  }

  /**
   * 키패드를 닫는다.
   * @param fieldId 필드 ID
   */
  close(fieldId: SecureKeypadFieldId) {
    const el = this.elementRegistry.get(fieldId);
    if (!el) {
      return;
    }

    console.log('# close fieldId', fieldId);

    el.blur();
  }

  /**
   * 키패드 값  초기화
   */
  clear(fieldId?: SecureKeypadFieldId) {
    const w = window as any;
    w.$ASTX2 = w.$ASTX2 ?? {};
    if (fieldId) {
      const el = this.elementRegistry.get(fieldId) as HTMLInputElement;
      if (!el) {
        return;
      }

      w.$ASTX2.clearE2EText(el, function () {
        console.log('[보안키패드]', '### clearE2EInput ASTX2', el.id);
        el.value = '';
      });
    } else {
      const idsArr = Array.from(this.elementRegistry.keys());

      idsArr.map((key) => {
        const el = this.elementRegistry.get(key) as HTMLInputElement;
        if (!el) {
          return;
        }

        w.$ASTX2.clearE2EText(el, function () {
          console.log('[보안키패드]', '### clearE2EInput ASTX2', el.id);
          el.value = '';
        });
      });
    }
  }

  /**
   * 암호화된 값 return
   */
  getEncData(): Promise<Record<string, string | Record<string, string>>> {
    return new Promise((resolve) => {
      const idsArr = [...Array.from(this.elementRegistry.keys())];

      // 확장 E2E 평문 map 생성
      let extE2EPlainMap = Object.fromEntries(
        idsArr
          .map((item) => {
            const el = this.elementRegistry.get(item) as HTMLInputElement;
            //E2E 필드 이고 값이 있을 때
            if (el.getAttribute('data-is-ext-e2e') && el?.value) {
              window.$ASTX2.setE2EText(el);
              console.log('### 확장 e2e value :', el?.value);
              return [item, formatUtils.replaceAll(el.value, ',', '')];
            }
            return;
          })
          .filter(Boolean) as [string, string][],
      );

      // Ajax 요청 (jQuery와 동일한 방식으로 fetch 사용)
      window.$ASTX2.getE2EDataIDs([...idsArr], function onSuccess(data: { [x: string]: string }) {
        if (Object.keys(extE2EPlainMap).length !== 0) {
          extE2EPlainMap = { ...extE2EPlainMap, encValue: data?.e2e_data2 };
        }

        console.log('### extE2EPlainMap : ', extE2EPlainMap);

        const encData = {
          encType: 'ASTX2', // TODO: Constants 화
          encValue: data?.e2e_data2,
          extE2EPlainMap,
        };

        resolve(encData);
      });
    });
  }

  /**
   *
   * @param fieldId
   * @returns
   */
  getOptions(fieldId: SecureKeypadFieldId): SecureKeypadFieldOption | undefined {
    return this.optionRegistry.get(fieldId as SecureKeypadFieldId);
  }

  forEachInput(callback: (el: HTMLElement) => void) {
    return this.elementRegistry.forEach(callback);
  }

  /**
   * 초기화를 한번만 수행한다.
   * @param ctx 컨텍스트
   * @returns
   */
  private initOnce(ctx: SecureKeypadContext) {
    if (this.initialized) {
      return;
    }

    console.log('### astx init');

    this.initialized = true;

    try {
      const opts: SecureKeypadFieldOption[] = ctx.options ?? [];

      if (!opts) {
        console.warn('options is null or undefined');
      }

      if (opts) {
        opts.forEach((opt) => {
          this.optionRegistry.set(opt.fieldId, opt);
        });
      }

      if (window.$ASTX2 && typeof window.$ASTX2.init === 'function') {
        // onSuccess 콜백 함수
        const onSuccess = (result: any) => {
          console.log('init 성공: ', JSON.stringify(result, null, 2));

          // init 성공 시 다음 함수(initE2E) 호출
          setTimeout(() => {
            this.handleInitE2E();
          }, 100);
        };

        // onFailure 콜백 함수
        const onFailure = (error: unknown) => {
          console.log('init 실패:\n', JSON.stringify(error, null, 2));

          if (window.$ASTX2 && typeof window.$ASTX2.getLastError === 'function') {
            let errno = window.$ASTX2.getLastError();
            if (errno == window.$ASTX2_CONST.ERROR_NOTINST) {
              // goto install page
              // TODO:
            } else {
              // error handling
              // TODO:
            }
          }
        };

        if (ctx.channel === Channel.IB && deviceUtils.isWin()) {
          window.$ASTX2.setOption({ e2eform: false });
          if (window.$ASTX2.init) {
            // init 함수에 콜백 인자 전달
            window.$ASTX2.init(onSuccess, onFailure);
          }
        }
      } else {
        console.log('window.$ASTX2.init 함수를 찾을 수 없습니다.');
      }
    } catch (err) {
      console.log('init 호출 중 오류: ', err);
    }
  }

  private handleInitE2E() {
    try {
      // 디버깅을 위해 window 객체 확인
      if (window.$ASTX2) {
        if (typeof window.$ASTX2.initE2E === 'function') {
          window.$ASTX2.initE2E();
        } else {
          console.log(
            'initE2E는 ' +
              typeof window.$ASTX2.initE2E +
              ' 타입입니다. 사용 가능한 메서드: ' +
              Object.keys(window.$ASTX2).join(', '),
          );
        }
      } else {
        console.log('window.$ASTX2 객체를 찾을 수 없습니다.');
      }
    } catch (err) {
      console.log('initE2E 호출 중 오류: ' + err);
    }
  }

  /**
   * 초기 기본 옵션과 필드 등록시 전달된 옵션을 merge한다.
   * @param fieldId 필드 ID
   * @param option 필드 등록 시 전달된 옵션
   * @returns
   */
  private mergeOptions(
    fieldId: string,
    option?: SecureKeypadFieldOption,
  ): SecureKeypadFieldOption | undefined {
    const defaultOption = this.optionRegistry.get(fieldId);
    if (!option) {
      return defaultOption;
    }

    return {
      ...defaultOption,
      ...option,
    };
  }
}
